import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface DashboardStats {
  journeys: { total: number; published: number };
  spotlights: { total: number; published: number };
  bookings: { total: number; inquiry: number; confirmed: number; deposit_paid: number; fully_paid: number };
  media: { total: number };
}

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get('/v1/admin/dashboard/stats'),
  });

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Journeys"
          value={stats?.journeys.total || 0}
          subtitle={`${stats?.journeys.published || 0} published`}
        />
        <StatCard
          title="Spotlights"
          value={stats?.spotlights.total || 0}
          subtitle={`${stats?.spotlights.published || 0} published`}
        />
        <StatCard
          title="Bookings"
          value={stats?.bookings.total || 0}
          subtitle={`${stats?.bookings.inquiry || 0} inquiries`}
        />
        <StatCard
          title="Media"
          value={stats?.media.total || 0}
          subtitle="images"
        />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-4">Booking Status</h2>
          <div className="space-y-3">
            <StatusBar label="Inquiries" value={stats?.bookings.inquiry || 0} color="yellow" />
            <StatusBar label="Confirmed" value={stats?.bookings.confirmed || 0} color="blue" />
            <StatusBar label="Deposit Paid" value={stats?.bookings.deposit_paid || 0} color="indigo" />
            <StatusBar label="Fully Paid" value={stats?.bookings.fully_paid || 0} color="green" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-4">Quick Actions</h2>
          <div className="space-y-2">
            <a href="/journeys/new" className="block w-full text-left px-4 py-2 rounded bg-gray-50 hover:bg-gray-100">
              + Create new journey
            </a>
            <a href="/spotlight/new" className="block w-full text-left px-4 py-2 rounded bg-gray-50 hover:bg-gray-100">
              + Write new spotlight
            </a>
            <a href="/media" className="block w-full text-left px-4 py-2 rounded bg-gray-50 hover:bg-gray-100">
              Upload media
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle }: { title: string; value: number; subtitle: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-3xl font-semibold text-gray-900 mt-2">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
    </div>
  );
}

function StatusBar({ label, value, color }: { label: string; value: number; color: string }) {
  const colorClasses: Record<string, string> = {
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    indigo: 'bg-indigo-100 text-indigo-800',
    green: 'bg-green-100 text-green-800',
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClasses[color]}`}>
        {value}
      </span>
    </div>
  );
}
